import { Controller, useForm } from "react-hook-form";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { states } from "../utils/states";
import { useDialog } from "../utils/isDialogOpen";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/reactQuery";
import { createBuilding } from "@/api/buildings/create-building";
import { getCompany } from "@/api/company/get-Company";
import { toast } from "sonner";

const createBuildingSchema = z.object({
  buildingName: z.string({ message: "Enter Building Name" }),
  address: z.string({ message: "Enter Building Address" }),
  letterIdBuilding: z
    .string({ message: "Enter letter id building" })
    .optional(),
  buildingCity: z.string(),
  buildingState: z.string(),
  buildingZip: z.string(),
  buildingCountry: z.string().optional(),
});

export default function RegisterCustomerInvoice() {
  const { isDialogOpen, setIsDialogOpen } = useDialog();

  type CreateBuildingType = z.infer<typeof createBuildingSchema>;

  const {
    control: buildingControl,
    register: buildingRegister,
    handleSubmit: handleBuildingSubmit,
    reset: resetBuilding,
    formState: { errors: buildingErrors, isSubmitting },
  } = useForm<CreateBuildingType>({
    resolver: zodResolver(createBuildingSchema),
  });

  const { mutateAsync: CreateBuildingFn } = useMutation({
    mutationKey: ["buildings"],
    mutationFn: createBuilding,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buildings"] });
    },
  });

  async function handleCreateBuilding(data: CreateBuildingType) {
    try {
      const companyRes = await getCompany(); // fetch the entire company object
      const companyId = companyRes.company[0].id;
      const { status } = await CreateBuildingFn({
        buildingName: data.buildingName,
        address: data.address,
        letterIdBuilding: data.letterIdBuilding,
        buildingCity: data.buildingCity,
        buildingState: data.buildingState,
        buildingZip: data.buildingZip,
        companyId: companyId,
      });
      if (status === 201) {
        toast.success("Building created successfully");
        resetBuilding();
        setIsDialogOpen(false);
      }
    } catch (err) {
      console.log(err);
      toast.error("Error creating building");
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogContent className="border-transparent bg-inherit">
        <DialogHeader>
          <DialogTitle className="text-black">
            Register New Customer
          </DialogTitle>
          <DialogDescription>
            Fill in to register a new customer
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleBuildingSubmit(handleCreateBuilding)}>
          <div className="flex space-x-4">
            {/* Container for building name input */}
            <div className="flex w-96 flex-col">
              <label
                htmlFor="buildingName"
                className="block text-sm font-medium text-black"
              >
                Customer Name
              </label>
              <Input
                {...buildingRegister("buildingName", {
                  required: "Building name is required",
                })}
                type="text"
                id="buildingName"
                autoComplete="off"
                className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
              />
              {buildingErrors.buildingName && (
                <span className="text-sm text-rose-500">
                  {buildingErrors.buildingName.message}
                </span>
              )}
            </div>

            {/* Container for letter ID input */}
            <div className="flex w-16 flex-col">
              <label
                htmlFor="letterIdBuilding"
                className="block text-sm font-medium text-black"
              >
                Letter Id
              </label>
              <Input
                {...buildingRegister("letterIdBuilding")}
                type="text"
                id="letterIdBuilding"
                autoComplete="off"
                className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
              />
              {buildingErrors.letterIdBuilding && (
                <span className="text-sm text-rose-500">
                  {buildingErrors.letterIdBuilding.message}
                </span>
              )}
            </div>
          </div>

          {/* Container for address input */}
          <div className="mt-4">
            <label
              htmlFor="address"
              className="block text-sm font-medium text-black"
            >
              Address
            </label>
            <Input
              {...buildingRegister("address", {
                required: "Building address is required",
              })}
              type="text"
              id="address"
              autoComplete="off"
              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
            />
            {buildingErrors.address && (
              <span className="text-sm text-rose-500">
                {buildingErrors.address.message}
              </span>
            )}
          </div>
          <div className="mt-4 flex space-x-4">
            <div>
              <label
                htmlFor="state"
                className="block text-sm font-medium text-black"
              >
                State
              </label>
              <Controller
                name="buildingState"
                control={buildingControl}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger
                      className={`mt-1 w-[80px] rounded-md border-zinc-300 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm ${
                        field.value ? "text-zinc-900" : "text-gray-500"
                      }`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="w-[80px]">
                      {states.map((state) => (
                        <SelectItem key={state.value} value={state.label}>
                          {state.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div>
              <label
                htmlFor="city"
                className="block text-sm font-medium text-black"
              >
                City
              </label>
              <Input
                type="text"
                id="buildingCity"
                autoComplete="off"
                className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                {...buildingRegister("buildingCity")}
              />
            </div>
            <div>
              <label
                htmlFor="zip"
                className="block text-sm font-medium text-black"
              >
                Zip
              </label>
              <Input
                type="text"
                id="buildingZip"
                autoComplete="off"
                className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
                {...buildingRegister("buildingZip")}
              />
            </div>
          </div>

          <div className="mt-4">
            <Button
              disabled={isSubmitting}
              className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
              type="submit"
            >
              Register Customer
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
